/**
 * Angebot · Einblicke (Migration 730): Typen und Formatierer fuer die Anzeige im Backoffice
 * (Kanban-Karte, Angebots-Verlauf). Rein, ohne Netz.
 *
 * Die Daten kommen als Rohwerte vom Server (`GET /api/events/:id/angebots-verlauf` → `einblicke`,
 * `GET /api/inquiries` → `tatsachen.aufrufeSeitVersand`/`letztesGeraet`); hier wird nur
 * formatiert. Erfasst wird ausschliesslich: Zeitpunkt, Geraeteklasse, Verweildauer, Lesefortschritt
 * (keine IP-Adresse, keine Browserkennung).
 *
 * Zeitzone: Europe/Berlin, ausdruecklich — die Zeitpunkte sind Betriebszeiten und sollen auf jedem
 * Geraet (und im Test) gleich lauten.
 */
export type Geraet = "handy" | "tablet" | "computer" | "unbekannt";

export interface EinblickAufruf {
  zeitpunkt: string;
  art: "seite" | "pdf";
  geraet: Geraet;
  versandNr: number;
  dauerSekunden: number | null;
  scrollProzent: number | null;
}

export interface EinblickProVersand {
  versandNr: number;
  versandAm: string | null;
  seitenaufrufe: number;
  pdfAufrufe: number;
  erster: string | null;
  letzter: string | null;
  /** Aufrufe vor der Umstellung auf Einzelaufrufe (nur Zähler, ohne Gerät/Dauer). Fehlt bei alten Antworten. */
  vorUmstellung?: number;
}

export interface EinblickEreignis {
  art: string;
  zeitpunkt: string;
}

export interface Einblicke {
  aufrufe: EinblickAufruf[];
  proVersand: EinblickProVersand[];
  ereignisse: EinblickEreignis[];
}

const ZONE = "Europe/Berlin";

export function geraetLabel(g: Geraet | null | undefined): string {
  switch (g) {
    case "handy":
      return "Handy";
    case "tablet":
      return "Tablet";
    case "computer":
      return "Computer";
    default:
      return "Gerät unbekannt";
  }
}

/** Material-Symbols-Name zur Geraeteklasse. */
export function geraetIcon(g: Geraet | null | undefined): string {
  switch (g) {
    case "handy":
      return "smartphone";
    case "tablet":
      return "tablet";
    case "computer":
      return "computer";
    default:
      return "devices_other";
  }
}

function teile(d: Date): { tag: string; monat: string; jahr: string; h: string; min: string } {
  const f = new Intl.DateTimeFormat("de-DE", {
    timeZone: ZONE,
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  });
  const p = Object.fromEntries(f.formatToParts(d).map((x) => [x.type, x.value]));
  return { tag: p.day!, monat: p.month!, jahr: p.year!, h: p.hour!, min: p.minute! };
}

function alsDate(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** "12.10." */
export function kurzDatum(iso: string | null | undefined): string | null {
  const d = alsDate(iso);
  if (!d) return null;
  const t = teile(d);
  return `${t.tag}.${t.monat}.`;
}

/** "12.10. 09:14" */
export function datumUhrzeit(iso: string | null | undefined): string | null {
  const d = alsDate(iso);
  if (!d) return null;
  const t = teile(d);
  return `${t.tag}.${t.monat}. ${t.h}:${t.min}`;
}

/** "heute 18:40" / "gestern 18:40" / "12.10. 18:40" - der Tag wird in Berliner Zeit verglichen. */
export function relativeZeit(iso: string | null | undefined, jetzt: Date = new Date()): string | null {
  const d = alsDate(iso);
  if (!d) return null;
  const t = teile(d);
  const n = teile(jetzt);
  const tagZahl = (x: { tag: string; monat: string; jahr: string }) => Date.UTC(Number(x.jahr), Number(x.monat) - 1, Number(x.tag));
  const diff = Math.round((tagZahl(n) - tagZahl(t)) / 86_400_000);
  const uhr = `${t.h}:${t.min}`;
  if (diff === 0) return `heute ${uhr}`;
  if (diff === 1) return `gestern ${uhr}`;
  return `${t.tag}.${t.monat}. ${uhr}`;
}

/** 130 → "2 Min. 10 Sek."; 45 → "45 Sek."; 3600 → "1 Std."; null → null. */
export function dauerText(sekunden: number | null | undefined): string | null {
  if (typeof sekunden !== "number" || !Number.isFinite(sekunden) || sekunden < 0) return null;
  const s = Math.round(sekunden);
  if (s < 60) return `${s} Sek.`;
  const min = Math.floor(s / 60);
  const rest = s % 60;
  if (min < 60) return rest === 0 ? `${min} Min.` : `${min} Min. ${rest} Sek.`;
  const std = Math.floor(min / 60);
  const m = min % 60;
  return m === 0 ? `${std} Std.` : `${std} Std. ${m} Min.`;
}

export const STATIONEN: ReadonlyArray<{ art: string; label: string; icon: string }> = [
  { art: "preis_gesehen", label: "Preis gesehen", icon: "payments" },
  { art: "zahlungsbedingungen_geoeffnet", label: "Zahlungsbedingungen geöffnet", icon: "receipt_long" },
  { art: "anmerkung_begonnen", label: "Anmerkung begonnen", icon: "edit_note" },
  { art: "variante_gewaehlt", label: "Variante gewählt", icon: "ads_click" },
  { art: "zusage_begonnen", label: "Zusage begonnen", icon: "task_alt" },
  { art: "pdf_geladen", label: "PDF geladen", icon: "picture_as_pdf" },
];

/** Der Satz je Versand: "Versand 2 (12.10.): 3 Seitenaufrufe, 1 PDF, erster 12.10. 09:14, letzter 14.10. 11:02". */
export function versandZeile(v: EinblickProVersand): string {
  const kopf = v.versandNr > 0 ? `Versand ${v.versandNr}` : "Vor der Zählung der Versände";
  const datum = kurzDatum(v.versandAm);
  const seiten = `${v.seitenaufrufe} ${v.seitenaufrufe === 1 ? "Seitenaufruf" : "Seitenaufrufe"}`;
  const pdf = `${v.pdfAufrufe} PDF`;
  const teile2 = [seiten, pdf];
  const erster = datumUhrzeit(v.erster);
  const letzter = datumUhrzeit(v.letzter);
  if (erster) teile2.push(`erster ${erster}`);
  if (letzter && letzter !== erster) teile2.push(`letzter ${letzter}`);
  const alt = v.vorUmstellung ?? 0;
  const nachsatz = alt > 0 ? ` — davon ${alt} vor der Umstellung (ohne Gerät und Dauer)` : "";
  return `${kopf}${datum ? ` (${datum})` : ""}: ${teile2.join(", ")}${nachsatz}`;
}

/**
 * Der Satz auf der Kanban-Karte: "3× seit Versand · zuletzt gestern 18:40". `null` = nichts
 * zeigen (nicht erhoben). Das Geraet haengt die Karte selbst mit Symbol an.
 */
export function kartenAufrufe(args: {
  versendet: boolean;
  seitVersand: number | null | undefined;
  insgesamt: number | null | undefined;
  zuletztAm: string | null | undefined;
  jetzt?: Date;
}): string | null {
  if (!args.versendet) return null;
  const n = args.seitVersand ?? args.insgesamt;
  if (n === null || n === undefined) return null;
  if (n === 0) return (args.insgesamt ?? 0) > 0 ? "Seit Versand nicht geöffnet" : "Nie geöffnet";
  const zuletzt = relativeZeit(args.zuletztAm, args.jetzt);
  return `${n}× seit Versand${zuletzt ? ` · zuletzt ${zuletzt}` : ""}`;
}
